import {
  Box,
  Chip,
  IconButton,
  Paper,
  Theme,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { useAppSelector } from "app/hooks";
import { LessonByUser } from "models/managerLesson";
import queryString from "query-string";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { selectLessonListByUser } from "../../ListLesson/listLessonSlice";

export interface ListVideoRecommendProps {
  openDrawer: boolean;
  setOpenDrawer: (e: any) => void;
}

const DEFAULT_FALLBACK_THUMB = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&auto=format&fit=crop&q=80";

export default function ListVideoRecommend({ openDrawer, setOpenDrawer }: ListVideoRecommendProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const listLesson = useAppSelector(selectLessonListByUser);
  const location: any = useLocation();
  const querySearchDate = queryString.parse(location.search);

  const useStyles = makeStyles((theme: Theme) => ({
    cardContainer: {
      backgroundColor: "#FFFFFF",
      borderRadius: 20,
      border: "1px solid #E2E8F0",
      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.04)",
      padding: 20,
      overflow: "hidden",
    },
    headerRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      paddingBottom: 16,
      marginBottom: 16,
      borderBottom: "1px solid #F1F5F9",
    },
    headerTitle: {
      fontWeight: 900,
      fontSize: "1rem",
      color: "#0F172A",
      letterSpacing: "0.01em",
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    itemCard: {
      backgroundColor: "#F8FAFC",
      borderRadius: 16,
      border: "1px solid #E2E8F0",
      padding: 12,
      marginBottom: 12,
      display: "flex",
      gap: 12,
      cursor: "pointer",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#FFFFFF",
        borderColor: "#C7D2FE",
        transform: "translateY(-2px)",
        boxShadow: "0 8px 20px rgba(79, 70, 229, 0.12)",
        "& $itemTitle": {
          color: "#4F46E5",
        },
      },
    },
    thumbWrapper: {
      position: "relative",
      width: 100,
      height: 72,
      borderRadius: 12,
      overflow: "hidden",
      flexShrink: 0,
      backgroundColor: "#0F172A",
    },
    thumbImg: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    playOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(15, 23, 42, 0.3)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#FFFFFF",
      opacity: 0.9,
    },
    itemContent: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      flex: 1,
      minWidth: 0,
    },
    itemCategory: {
      fontSize: "0.7rem",
      fontWeight: 900,
      color: "#4F46E5",
      backgroundColor: "#EEF2FF",
      padding: "2px 8px",
      borderRadius: 6,
      display: "inline-block",
      maxWidth: "fit-content",
      textTransform: "uppercase",
    },
    itemTitle: {
      fontSize: "0.85rem",
      fontWeight: 800,
      color: "#0F172A",
      lineHeight: 1.3,
      marginTop: 4,
      marginBottom: 4,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      transition: "color 0.2s ease",
    },
    metaRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontSize: "0.75rem",
      color: "#64748B",
    },
  }));

  const classes = useStyles();

  const rawItems: any[] = listLesson?.content || [];

  return (
    <Paper className={classes.cardContainer} elevation={0}>
      <Box className={classes.headerRow}>
        <Typography className={classes.headerTitle}>
          <MenuBookIcon style={{ fontSize: 20, color: "#4F46E5" }} />
          {t("home.detaillesson.danhsachbaihockhac") || "DANH SÁCH BÀI HỌC KHÁC"}
        </Typography>
        <Chip
          label={`${rawItems.length} BÀI HỌC`}
          size="small"
          style={{ backgroundColor: "#F1F5F9", color: "#475569", fontWeight: 800, fontSize: "0.7rem" }}
        />
      </Box>

      {rawItems.map((item: any) => {
        const pubDateStr = String(item?.lession?.publishDate || "20260805");
        const day = pubDateStr.slice(-2);
        const month = pubDateStr.slice(4, 6);
        const year = pubDateStr.slice(0, 4);

        const thumb =
          item?.lession?.attachment?.urlThumbnail ||
          item?.lession?.powerPointFileList?.[0]?.powerPointFile?.urlThumbnail ||
          DEFAULT_FALLBACK_THUMB;

        const isWatched = item?.watched ?? item?.lessionUsers?.watched;

        return (
          <Box
            key={item.id}
            className={classes.itemCard}
            onClick={() =>
              navigate({
                pathname: `/lesson/${item.lessionId || item.id}`,
                search: `year=${querySearchDate.year || "2026"}&month=${querySearchDate.month || "08"}&attachmentID=${item?.lession?.attachmentID}`,
              })
            }
          >
            {/* Thumbnail */}
            <Box className={classes.thumbWrapper}>
              <img
                src={thumb}
                alt="Thumbnail"
                className={classes.thumbImg}
                onError={(e: any) => {
                  e.target.onerror = null;
                  e.target.src = DEFAULT_FALLBACK_THUMB;
                }}
              />
              <Box className={classes.playOverlay}>
                <PlayCircleFilledIcon style={{ fontSize: 24 }} />
              </Box>
            </Box>

            {/* Content */}
            <Box className={classes.itemContent}>
              <Box className={classes.itemCategory}>
                {item?.lession?.lessionCategory?.name || "BÀI HỌC"}
              </Box>

              <Tooltip title={item?.lession?.lessionName || ""}>
                <Typography className={classes.itemTitle}>
                  {item?.lession?.lessionName}
                </Typography>
              </Tooltip>

              <Box className={classes.metaRow}>
                <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                  <CalendarTodayIcon style={{ fontSize: 12, color: "#94A3B8" }} />
                  <span>{day}/{month}/{year}</span>
                </Box>

                {isWatched ? (
                  <Chip
                    icon={<CheckCircleIcon style={{ fontSize: 12, color: "#16A34A" }} />}
                    label="Đã xem"
                    size="small"
                    style={{ height: 20, fontSize: "0.65rem", backgroundColor: "#F0FDF4", color: "#16A34A", fontWeight: 800 }}
                  />
                ) : (
                  <Chip
                    icon={<VisibilityIcon style={{ fontSize: 12, color: "#4F46E5" }} />}
                    label="Chưa xem"
                    size="small"
                    style={{ height: 20, fontSize: "0.65rem", backgroundColor: "#EEF2FF", color: "#4F46E5", fontWeight: 800 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
}

